import axios, { AxiosError } from 'axios';
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { API_BASE_URL, BACKEND_BASE_URL, WEBSOCKET_URL } from '../config/runtime';
import { attachUserContext, captureApiFailure } from '../observability';
export { API_BASE_URL, BACKEND_BASE_URL, WEBSOCKET_URL };
export const PRIVACY_POLICY_URL = `${BACKEND_BASE_URL}/privacy-policy`;
// Deprecated: N8N_WEBHOOK_URL is no longer used - messages are routed through the backend SmartDocs bridge.
// export const N8N_WEBHOOK_URL = import.meta.env.VITE_CONTRACTOR_N8N_WEBHOOK_URL || import.meta.env.VITE_SMARTDOCS_N8N_WEBHOOK_URL;

const toPlainHeaders = (headers: InternalAxiosRequestConfig['headers']): Record<string, string> => {
  const rawHeaders = typeof headers?.toJSON === 'function' ? headers.toJSON() : headers || {};
  return Object.entries(rawHeaders).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc;
    }
    acc[key] = String(value);
    return acc;
  }, {});
};

const getHeaderValue = (headers: Record<string, string>, headerName: string): string | undefined => {
  const normalizedHeaderName = headerName.toLowerCase();
  const matchingEntry = Object.entries(headers).find(([key]) => key.toLowerCase() === normalizedHeaderName);
  return matchingEntry?.[1];
};

const isJsonContentType = (headers: Record<string, string>): boolean => {
  const contentType = getHeaderValue(headers, 'Content-Type');
  return typeof contentType === 'string' && contentType.toLowerCase().includes('application/json');
};

const getResponseType = (responseType: InternalAxiosRequestConfig['responseType']) => {
  if (responseType === 'arraybuffer' || responseType === 'blob' || responseType === 'text' || responseType === 'document') {
    return responseType;
  }
  return 'json';
};

export const resolveNativeTimeout = (timeout: number | undefined): number => timeout ?? 10000;

export const normalizeNativeRequestData = (
  data: InternalAxiosRequestConfig['data'],
  headers: Record<string, string>
) => {
  if (data == null) {
    return data;
  }

  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
    return data.toString();
  }

  if (!isJsonContentType(headers) || typeof data !== 'string') {
    return data;
  }

  const trimmedData = data.trim();
  if (!trimmedData) {
    return data;
  }

  try {
    return JSON.parse(trimmedData);
  } catch {
    return data;
  }
};

const nativeHttpAdapter: AxiosAdapter = async (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
  const url = axios.getUri(config);
  const nativeTimeout = resolveNativeTimeout(config.timeout);
  const headers = toPlainHeaders(config.headers);
  const response = await CapacitorHttp.request({
    url,
    method: (config.method || 'get').toUpperCase(),
    headers,
    data: normalizeNativeRequestData(config.data, headers),
    connectTimeout: nativeTimeout,
    readTimeout: nativeTimeout,
    responseType: getResponseType(config.responseType),
  });

  const axiosResponse: AxiosResponse = {
    data: response.data,
    status: response.status,
    statusText: String(response.status),
    headers: response.headers,
    config,
    request: { url, native: true },
  };

  const validateStatus = config.validateStatus || ((status: number) => status >= 200 && status < 300);
  if (!validateStatus(response.status)) {
    throw new AxiosError(
      `Request failed with status code ${response.status}`,
      response.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
      config,
      axiosResponse.request,
      axiosResponse
    );
  }

  return axiosResponse;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Keep this for cookies if they're needed
  timeout: 10000, // Set a reasonable timeout
  adapter: Capacitor.isNativePlatform() ? nativeHttpAdapter : undefined,
});

// Flag to prevent multiple redirects
let isRedirecting = false;
// Flag to prevent multiple refresh attempts
let isRefreshing = false;

const makeRequestId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const PUBLIC_AUTH_ROUTES = ['/signin', '/forgot-password', '/reset-password'];

export const isPublicAuthRoute = (pathname: string): boolean =>
  PUBLIC_AUTH_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

// Add a request interceptor to add the auth token
api.interceptors.request.use(
  (config) => {
    void attachUserContext();
    const token = localStorage.getItem('token');
    const requestId = makeRequestId();
    config.headers['X-Request-ID'] = requestId;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const requestId = error.response?.headers?.['x-request-id'] || originalRequest?.headers?.['X-Request-ID'];
    captureApiFailure(error, {
      request: {
        url: originalRequest?.url,
        method: originalRequest?.method,
        request_id: requestId,
      },
      response: {
        status: error.response?.status,
      },
    });

    // Handle specific error status codes
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Try to refresh token if this is not a refresh request itself
          if (!originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !isRefreshing) {
            originalRequest._retry = true;
            isRefreshing = true;

            try {
              const refreshToken = localStorage.getItem('refresh_token');
              if (!refreshToken) {
                throw new Error('No refresh token available');
              }

              // Import authApi here to avoid circular dependency
              const { authApi } = await import('./authApi');
              const response = await authApi.refresh();

              if (response.success) {
                // Update the token in the original request
                const newToken = response.data.token;
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                // Retry the original request with the new token
                return api(originalRequest);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              captureApiFailure(refreshError, {
                request: { url: '/auth/refresh', method: 'post' },
                response: { status: 401 },
              });
              // Public auth routes must remain reachable (e.g. reset-password email links).
              // Avoid hijacking navigation back to sign-in while user is resetting password.
              if (!isPublicAuthRoute(window.location.pathname) && !isRedirecting) {
                isRedirecting = true;
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
                // Add a small delay to prevent race conditions
                setTimeout(() => {
                  window.location.href = '/signin';
                  isRedirecting = false;
                }, 100);
              }
            } finally {
              isRefreshing = false;
            }
          } else {
            console.warn('Unauthorized request (401) - Token refresh failed or not available');
          }
          break;
        case 0: // This can happen with CORS errors
        case 403: // Forbidden
        case 500: // Server errors
        case 502: // Bad Gateway
        case 503: // Service Unavailable
          console.warn(`API Error: ${error.response.status}`, error.message);
          break;
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.warn('Network error - no response received:', error.request);
    }
    return Promise.reject(error);
  }
);

export default api;
